/**
 * The one week's notice that the trial is ending.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHY A BAND AND NOT A DIALOG
 *
 * There is already a dialog for when writing is blocked, and it is the right
 * shape for that: something has stopped, and the person needs to deal with it
 * before carrying on. A trial with six days left has stopped nothing. A modal
 * would interrupt work to deliver news that does not require any action today,
 * and the second time it appeared it would be dismissed without reading.
 *
 * So it sits above the page, states the date, and is dismissible.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * DISMISSAL IS PER DAY, NOT FOREVER AND NOT PER PAGE
 *
 * Per page is nagging: closing it and having it return on the next click
 * teaches somebody the close button is broken. Forever is worse in the other
 * direction, because the whole point is that the number changes, and a band
 * dismissed on day seven has nothing to say about day one.
 *
 * So the key carries the day. Dismiss it today and it is gone today; tomorrow
 * is a different number and a different message, and by then it is nearer.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * IT DOES NOT APPEAR WHILE THERE IS PLENTY OF TIME
 *
 * Seven days, which is the same threshold the email uses. A notice that runs
 * for the whole trial is furniture, and furniture is not read. This is the
 * point where somebody can still choose calmly, which is the only moment a
 * warning is worth anything to either side.
 */

import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarClock, X } from 'lucide-react';
import { Button } from '@/components/shadcn/button';
import { useAuth } from '@/contexts/AuthContext';
import { useI18n } from '@/contexts/I18nContext';
import { cn } from '@/lib/utils';

/** The same week the trial-ending email uses. See jobs/dunning.ts. */
const WARN_WITHIN_DAYS = 7;

export function TrialEndingBand() {
  const { t, lang } = useI18n();
  const { entitlements } = useAuth();
  const navigate = useNavigate();
  const [dismissed, setDismissed] = useState<string | null>(null);

  const daysLeft = useMemo(() => {
    const ends = entitlements?.trial_ends_at;
    if (!ends) return null;
    const ms = new Date(ends).getTime() - Date.now();
    if (Number.isNaN(ms)) return null;
    /* Rounded up: eleven hours left is the last day, and telling somebody they
       have zero days while the product still works is a lie in the direction
       that costs a customer. */
    return Math.max(0, Math.ceil(ms / 86_400_000));
  }, [entitlements?.trial_ends_at]);

  const key = daysLeft === null ? '' : `trialband-${new Date().toDateString()}-${daysLeft}`;

  const hidden =
    !entitlements?.open_trial ||
    entitlements.subscribed ||
    daysLeft === null ||
    daysLeft > WARN_WITHIN_DAYS ||
    dismissed === key ||
    (() => {
      try {
        return sessionStorage.getItem(key) === '1';
      } catch {
        return false;
      }
    })();

  if (hidden) return null;

  const endsOn = entitlements?.trial_ends_at
    ? new Date(entitlements.trial_ends_at).toLocaleDateString(lang === 'ar' ? 'ar-EG' : 'en-GB', {
        day: 'numeric',
        month: 'long',
      })
    : '';

  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-3 border-b px-4 py-2.5 text-sm',
        /* Warm, not red. Nothing is broken and nothing has been taken away: a
           red band for a trial ending on schedule is the product crying wolf
           about its own pricing, and the next red band it shows will be read
           the same way. */
        'border-amber-500/25 bg-amber-500/10 text-amber-900 dark:text-amber-100',
      )}
      role="status"
    >
      <CalendarClock className="size-4 shrink-0" aria-hidden />
      <p className="min-w-0 flex-1">
        <span className="font-medium">
          {daysLeft <= 0
            ? t('trial_last_day')
            : t('trial_ending_title').replace('{n}', String(daysLeft))}
        </span>
        {endsOn ? <span className="opacity-70"> · {endsOn}</span> : null}
        {/* The offer, not just the deadline. A deadline on its own is pressure;
            the reason to act early is what makes it an offer. */}
        <span className="ms-2 opacity-90">{t('trial_ending_body')}</span>
      </p>
      <Button size="sm" onClick={() => navigate('/billing')}>
        {t('trial_ending_cta')}
      </Button>
      <Button
        variant="ghost"
        size="icon"
        aria-label={t('dismiss')}
        className="size-7 shrink-0"
        onClick={() => {
          setDismissed(key);
          try {
            sessionStorage.setItem(key, '1');
          } catch {
            /* Private mode. The band stays gone for this render either way. */
          }
        }}
      >
        <X className="size-4" aria-hidden />
      </Button>
    </div>
  );
}
